// React Hooks for Tasks
// Data fetching and mutations for task instances

import { useState, useEffect } from 'react';
import { taskService } from '../services/taskService';

/**
 * Hook to fetch all tasks with optional filters
 * @param {boolean} enabled - When false, skips fetch (empty list); use until org context is ready
 */
export function useTasks(filters = {}, enabled = true) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getAllTasks(filters);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setTasks([]);
      setLoading(false);
      setError(null);
      return;
    }
    loadTasks();
  }, [JSON.stringify(filters), enabled]);

  const refresh = () => {
    if (!enabled) return;
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook to fetch a single task by ID
 */
export function useTask(taskId) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTask = async () => {
    if (!taskId) {
      setTask(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTask(taskId);
      setTask(data);
    } catch (err) {
      console.error('Error loading task:', err);
      setError(err.message);
      setTask(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
  }, [taskId]);

  const refresh = () => {
    loadTask();
  };

  return { task, loading, error, refresh };
}

const TASKS_LOAD_TIMEOUT_MS = 25000;

/**
 * Hook to fetch user's assigned tasks.
 * Uses a timeout so the UI never stays on "Loading tasks..." forever if Firestore or groups hang.
 */
export function useUserTasks(userId, filters = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    if (!userId) {
      setTasks([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Loading tasks timed out. Check your connection or try again.')), TASKS_LOAD_TIMEOUT_MS)
      );
      const data = await Promise.race([
        taskService.getTasksByUser(userId, filters),
        timeoutPromise
      ]);
      setTasks(data || []);
    } catch (err) {
      console.error('Error loading user tasks:', err);
      setError(err.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    loadTasks();
  }, [userId, filtersKey]);

  const refresh = () => {
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook to fetch overdue tasks
 */
export function useOverdueTasks(filters = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getOverdueTasks(filters);
      setTasks(data);
    } catch (err) {
      console.error('Error loading overdue tasks:', err);
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [JSON.stringify(filters)]);

  const refresh = () => {
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook to fetch tasks by status
 */
export function useTasksByStatus(status, filters = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    if (!status) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTasksByStatus(status, filters);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks by status:', err);
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [status, JSON.stringify(filters)]);

  const refresh = () => {
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook to fetch tasks by template
 */
export function useTasksByTemplate(templateId, filters = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    if (!templateId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTasksByTemplate(templateId, filters);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks by template:', err);
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [templateId, JSON.stringify(filters)]);

  const refresh = () => {
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook to fetch tasks by package
 */
export function useTasksByPackage(packageId) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTasks = async () => {
    if (!packageId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTasksByPackage(packageId);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks by package:', err);
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [packageId]);

  const refresh = () => {
    loadTasks();
  };

  return { tasks, loading, error, refresh };
}

/**
 * Hook for task mutations (create, update, execute)
 */
export function useTaskMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createTask = async (taskData, userId) => {
    try {
      setLoading(true);
      setError(null);
      const taskId = await taskService.createTask(taskData, userId);
      return taskId;
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createFromTemplate = async (templateId, customContext, assignedTo, assignedToName, scheduledStart, userId) => {
    try {
      setLoading(true);
      setError(null);
      const taskId = await taskService.createTaskFromTemplate(
        templateId,
        customContext,
        assignedTo,
        assignedToName,
        scheduledStart,
        userId
      );
      return taskId;
    } catch (err) {
      console.error('Error creating task from template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const startTask = async (taskId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.startTask(taskId, userId);
    } catch (err) {
      console.error('Error starting task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeTask = async (taskId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.acknowledgeTask(taskId, userId);
    } catch (err) {
      console.error('Error acknowledging task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const startProtocol = async (taskId, protocolIndex, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.startProtocol(taskId, protocolIndex, userId);
    } catch (err) {
      console.error('Error starting protocol:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async (taskId, protocolIndex, stepIndex, userId, stepData) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.completeStep(taskId, protocolIndex, stepIndex, userId, stepData);
    } catch (err) {
      console.error('Error completing step:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const skipStep = async (taskId, protocolIndex, stepIndex, userId, skipReason) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.skipStep(taskId, protocolIndex, stepIndex, userId, skipReason);
    } catch (err) {
      console.error('Error skipping step:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeProtocol = async (taskId, protocolIndex, userId, protocolData) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.completeProtocol(taskId, protocolIndex, userId, protocolData);
    } catch (err) {
      console.error('Error completing protocol:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async (taskId, userId, taskData) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.completeTask(taskId, userId, taskData);
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reassignTask = async (taskId, newUserId, newUserName, reason, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.reassignTask(taskId, newUserId, newUserName, reason, userId);
    } catch (err) {
      console.error('Error reassigning task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelTask = async (taskId, reason, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.cancelTask(taskId, reason, userId);
    } catch (err) {
      console.error('Error cancelling task:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateContext = async (taskId, contextUpdates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.updateTaskContext(taskId, contextUpdates, userId);
    } catch (err) {
      console.error('Error updating task context:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePriority = async (taskId, priority, urgency, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.updateTaskPriority(taskId, priority, urgency, userId);
    } catch (err) {
      console.error('Error updating task priority:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setStepActionCompleted = async (taskId, protocolIndex, stepIndex, actionIndex, completed, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.setStepActionCompleted(taskId, protocolIndex, stepIndex, actionIndex, completed, userId);
    } catch (err) {
      console.error('Error setting step action completed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTask,
    createFromTemplate,
    startTask,
    acknowledgeTask,
    startProtocol,
    completeStep,
    skipStep,
    completeProtocol,
    completeTask,
    reassignTask,
    cancelTask,
    updateContext,
    updatePriority,
    setStepActionCompleted,
    loading,
    error
  };
}

/**
 * Hook to fetch task statistics
 */
export function useTaskStatistics(filters = {}) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTaskStatistics(filters);
      setStats(data);
    } catch (err) {
      console.error('Error loading task statistics:', err);
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [JSON.stringify(filters)]);

  const refresh = () => {
    loadStats();
  };

  return { stats, loading, error, refresh };
}

/**
 * Hook to fetch task execution log
 */
export function useTaskLog(taskId) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLog = async () => {
    if (!taskId) {
      setLog([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskService.getTaskLog(taskId);
      setLog(data);
    } catch (err) {
      console.error('Error loading task log:', err);
      setError(err.message);
      setLog([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLog();
  }, [taskId]);

  const refresh = () => {
    loadLog();
  };

  return { log, loading, error, refresh };
}

